import React from 'react';
import styled from 'styled-components';
import type { AnsweringServicePayer } from '@/app-package';
import { AnsweringServiceType } from '@bb/domain/dist/types';
import { ViewingLevel } from 'Router/pages/SubscriptionsPaymentsPage';
import AnsweringServiceCard from './AnsweringServiceCard';
import SupraOneCard from './SupraOneCard';

export interface AvailableServicesSectionProps {
  /** Who pays for the Answering Service subscription */
  answeringServicePayer: AnsweringServicePayer;
  /** What type of Answering Service this user/brokerge has (or is eligible to sign up for) */
  answeringServiceType: AnsweringServiceType;
  /** Whether the SUPRA_MVP flag is turned on granting access to all Supra One features for this org. */
  canUseSupraOne: boolean;
  /** Whether there was an error fetching the Answering Service subscription data. */
  fetchErrorAnsweringService: boolean;
  /** Whether there is an already active Answering Service subscription for this user/brokerage */
  hasActiveAnsweringService: boolean;
  /** Whether there is an already active Supra One subscription for this user */
  hasActiveSupraSubscription: boolean;
  /** Callback when LCS or Appointment Desk Learn More is clicked */
  onAnsweringServiceClick?: () => void;
  /** Whether this is an indivdual user or for a brokerage viewing the page */
  viewingLevel: ViewingLevel;
}

/**
 * The "Available Services" section of the Subscription & Payments page
 * displaying all new subscriptions that the user/brokerage is eligible to
 * self-sign up for in the web app.
 */
const AvailableServicesSection: React.FC<AvailableServicesSectionProps> = ({
  // If no explicit answering service payer is passed in, we default
  // to 'user' to allow a user or brokerage to pay for it themself.
  answeringServicePayer = 'user',
  answeringServiceType = AnsweringServiceType.LiveConciergePlus,
  canUseSupraOne,
  fetchErrorAnsweringService,
  hasActiveAnsweringService = false,
  hasActiveSupraSubscription,
  onAnsweringServiceClick,
  viewingLevel,
}) => {
  /** Whether the user/brokerage is currently actively paying for a LCS subscription for themself. */
  const selfPaysForLCS =
    viewingLevel === answeringServicePayer ||
    (viewingLevel === 'org' && answeringServicePayer === 'brokerage');

  // If the user has no available new services to start, don't display anything
  if (selfPaysForLCS && hasActiveSupraSubscription) {
    return null;
  }

  return (
    <Container>
      <Title>Available Services</Title>
      <CardsContainer>
        {!hasActiveAnsweringService &&
          answeringServiceType !== AnsweringServiceType.Legacy && (
            <AnsweringServiceCard
              fetchDataError={fetchErrorAnsweringService}
              isSubscribed={false}
              answeringServiceType={answeringServiceType}
              answeringServicePayer={answeringServicePayer}
              onButtonClick={onAnsweringServiceClick}
            />
          )}
        {!hasActiveSupraSubscription && canUseSupraOne && (
          <SupraOneCard isSubscribed={false} />
        )}
      </CardsContainer>
    </Container>
  );
};

export default AvailableServicesSection;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #2b2b2b;
  margin: 0;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
